'use client';

import { useEffect, useId, useState, type ReactNode } from 'react';
import { Modal } from './modal';
import { Button } from './button';
import { Input } from '@heyroot/ui/primitives/Input';

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  /** Body / message. */
  children?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  /** Destructive / irreversible action: red CTA + role="alertdialog". */
  destructive?: boolean;
  /** Disables both actions and shows a spinner on Confirm while the action runs. */
  isLoading?: boolean;
  /**
   * Type-to-confirm tier (GATE B.1). When set, the user must type this exact
   * string before Confirm enables — used for irreversible actions (delete
   * tenant, void payment). Omit for the simple-confirm tier.
   */
  confirmText?: string;
  /**
   * Reason-required tier (MEGA-5 piece D). When true, renders a required
   * textarea and calls `onConfirmWithReason` (not `onConfirm`) on confirm --
   * Confirm stays disabled until the trimmed reason's length is within
   * [reasonMinLength, reasonMaxLength]. Used for admin actions that must
   * carry an audited justification (force-complete/reopen an onboarding step).
   */
  reasonRequired?: boolean;
  onConfirmWithReason?: (reason: string) => void;
  reasonMinLength?: number;
  reasonMaxLength?: number;
  reasonLabel?: ReactNode;
  /**
   * F15 (#12716 PR-6): additional externally-computed disabled condition,
   * OR'd with the dialog's own internal derivation (type-to-confirm /
   * reason-required checks) below. Lets a caller gate Confirm (and Enter,
   * which submits the same button) on state this dialog has no visibility
   * into -- e.g. series reschedule requires both a date and a time picked.
   */
  confirmDisabled?: boolean;
}

/**
 * Tiered confirmation dialog (GATE B.1) with Cancel focused by default (GATE B.2).
 * - simple tier: just Cancel + Confirm.
 * - type-to-confirm tier: pass `confirmText`; Confirm stays disabled until it matches.
 * Destructive actions get a red Confirm + `role="alertdialog"`.
 */
export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  isLoading = false,
  confirmText,
  reasonRequired = false,
  onConfirmWithReason,
  reasonMinLength = 1,
  reasonMaxLength = 500,
  reasonLabel = 'Reason',
  confirmDisabled: confirmDisabledProp = false,
}: ConfirmModalProps) {
  const inputId = useId();
  const reasonId = useId();
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  // Reset the type-to-confirm and reason fields whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setTyped('');
      setReason('');
    }
  }, [open]);

  const needsType = Boolean(confirmText);
  const typeMatched = !needsType || typed.trim() === confirmText;
  const trimmedReasonLength = reason.trim().length;
  const reasonValid =
    !reasonRequired ||
    (trimmedReasonLength >= reasonMinLength && trimmedReasonLength <= reasonMaxLength);
  const confirmDisabled = isLoading || !typeMatched || !reasonValid || confirmDisabledProp;

  function handleConfirmClick() {
    if (reasonRequired) {
      onConfirmWithReason?.(reason.trim());
    } else {
      onConfirm();
    }
  }

  return (
    <Modal
      open={open}
      // While the action runs, Escape must not dismiss the dialog either --
      // Cancel and the backdrop are already disabled, and a caller may be
      // awaiting a PIN prompt or a request whose result the dialog reports.
      onClose={() => {
        if (!isLoading) onClose();
      }}
      title={title}
      role={destructive ? 'alertdialog' : 'dialog'}
      closeOnBackdrop={!isLoading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isLoading} data-autofocus>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={handleConfirmClick}
            loading={isLoading}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {reasonRequired && (
        <div className="mt-4 flex flex-col gap-1.5">
          <label htmlFor={reasonId} className="text-body-sm text-primary">
            {reasonLabel}
          </label>
          <textarea
            id={reasonId}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={reasonMaxLength}
            rows={3}
            className="w-full rounded-control border border-border bg-surface px-3 py-2 text-body-sm text-primary"
          />
        </div>
      )}
      {needsType && (
        <div className="mt-4 flex flex-col gap-1.5">
          <label htmlFor={inputId} className="text-body-sm text-primary">
            Type <span className="text-body-sm-emphasis">{confirmText}</span> to confirm
          </label>
          <Input
            id={inputId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            aria-label={`Type ${confirmText} to confirm`}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
          />
        </div>
      )}
    </Modal>
  );
}
